import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useApiLocale } from '../../hooks/useApiLocale';
import { analyticsService, type AnalyticsPageCategory } from '../../services/analytics';
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  type CookieConsentPreferences
} from '../../services/cookieConsent';
import { useAuthStore } from '../../stores/authStore';

const LOCALE_SEGMENTS = new Set(['en', 'it', 'es', 'fr', 'de']);

const removeLocalePrefix = (pathname: string): string => {
  const normalizedPathname = pathname || '/';
  const segments = normalizedPathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  if (!LOCALE_SEGMENTS.has(segments[0])) {
    return normalizedPathname;
  }

  return segments.length === 1 ? '/' : `/${segments.slice(1).join('/')}`;
};

const getPageCategory = (pathname: string): AnalyticsPageCategory => {
  const route = removeLocalePrefix(pathname);

  if (route === '/') {
    return 'home';
  }

  if (route.startsWith('/login')) {
    return 'auth';
  }

  if (route.startsWith('/codex')) {
    return 'codex';
  }

  if (route.startsWith('/toolbox')) {
    return 'toolbox';
  }

  if (route.startsWith('/logbook')) {
    return 'logbook';
  }

  if (route.startsWith('/onboarding')) {
    return 'onboarding';
  }

  if (route.startsWith('/whats-on-now')) {
    return 'planner';
  }

  return 'unknown';
};

const normalizeRoute = (pathname: string): string => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

export const AnalyticsRouteTracker = () => {
  const location = useLocation();
  const apiLocale = useApiLocale();
  const { isAuthenticated, idToken, user } = useAuthStore();

  const trackCurrentPage = () => {
    analyticsService.trackPageView({
      route: normalizeRoute(location.pathname),
      pageCategory: getPageCategory(location.pathname),
      locale: apiLocale,
      metadata: {
        isAuthenticated,
        hasQueryString: Boolean(location.search)
      }
    });
  };

  useEffect(() => {
    analyticsService.setAuthContext({
      isAuthenticated,
      idToken,
      userId: user?.userId || null,
      email: user?.signInDetails?.loginId || null
    });
  }, [idToken, isAuthenticated, user]);

  useEffect(() => {
    trackCurrentPage();
  }, [apiLocale, isAuthenticated, location.pathname, location.search]);

  useEffect(() => {
    const trackAfterConsentGrant = (event: Event) => {
      const consentEvent = event as CustomEvent<CookieConsentPreferences>;
      if (consentEvent.detail.analytics) {
        trackCurrentPage();
      }
    };

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, trackAfterConsentGrant);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, trackAfterConsentGrant);
  }, [apiLocale, isAuthenticated, location.pathname, location.search]);

  return null;
};

export default AnalyticsRouteTracker;
